import React, { useMemo, useState } from "react";
import "./BoxWidget.css";
import { useAppTheme } from "../context/ThemeContext";

const buildLinearGradient = (angle, colors) => {
  const safeAngle = Number.isFinite(Number(angle)) ? Number(angle) : 0;
  const stops = Array.isArray(colors) ? colors.filter(Boolean) : [];
  if (stops.length < 2) return null;

  return `linear-gradient(${safeAngle}deg, ${stops.join(", ")})`;
};

const BoxWidget = ({
  id,
  draggable,
  title = "Box Widget",

  backgroundType = "gradient",
  backgroundColor = "rgba(18, 28, 40, 0.9)",
  gradientAngle = 135,
  gradientColors = ["rgba(56, 74, 94, 0.95)", "rgba(22, 31, 45, 0.95)"],

  backgroundColorLight,
  gradientColorsLight,

  opacity = 1,

  borderRadius = 16,
  borderWidth = 1,
  borderColor = "rgba(255, 255, 255, 0.08)",

  borderColorLight,

  shadowEnabled = true,
  shadowOffsetX = 0,
  shadowOffsetY = 10,
  shadowBlur = 30,
  shadowSpread = 0,
  shadowColor = "rgba(0, 0, 0, 0.35)",

  shadowColorLight,

  backdropBlur = 0,
}) => {
  const [cursorState, setCursorState] = useState("idle");

  const { mode } = useAppTheme();
  const isLight = mode === "light";
  const finalBackgroundColor = isLight
    ? backgroundColorLight || "rgba(255,255,255,0.9)"
    : backgroundColor;

  const finalGradientColors = isLight
    ? gradientColorsLight || gradientColors
    : gradientColors;

  const finalBorderColor = isLight
    ? borderColorLight || "rgba(0,0,0,0.15)"
    : borderColor;

  const finalShadowColor = isLight
    ? shadowColorLight || "rgba(0,0,0,0.15)"
    : shadowColor;

  const gradient =
    backgroundType === "gradient"
      ? buildLinearGradient(gradientAngle, finalGradientColors)
      : null;

  const cssVars = useMemo(() => {
    const op = Number.isFinite(Number(opacity)) ? Number(opacity) : 1;

    const br = `${Number(borderRadius) || 0}px`;

    const bw = Number(borderWidth) || 0;
    const border = bw > 0 ? `${bw}px solid ${finalBorderColor}` : "none";

    const shadow = shadowEnabled
      ? `${Number(shadowOffsetX) || 0}px ${
          Number(shadowOffsetY) || 0
        }px ${Number(shadowBlur) || 0}px ${
          Number(shadowSpread) || 0
        }px ${finalShadowColor}`
      : "none";

    const blurPx =
      (Number(backdropBlur) || 0) > 0 ? `${Number(backdropBlur)}px` : "0px";

    return {
      "--boxw-opacity": op,
      "--boxw-border-radius": br,
      "--boxw-border": border,
      "--boxw-shadow": shadow,

      "--boxw-bg-color": finalBackgroundColor,
      "--boxw-bg-image":
        backgroundType === "gradient" && gradient ? gradient : "none",

      "--boxw-backdrop-blur": blurPx,
    };
  }, [
    opacity,
    borderRadius,
    borderWidth,
    finalBorderColor,
    shadowEnabled,
    shadowOffsetX,
    shadowOffsetY,
    shadowBlur,
    shadowSpread,
    finalShadowColor,
    backgroundType,
    finalBackgroundColor,
    gradient,
    backdropBlur,
  ]);

  const showLabel = typeof id === "string" && id.startsWith("base");


  const cursorClass = draggable
    ? cursorState === "grabbing"
      ? "box-widget--grabbing"
      : "box-widget--grab"
    : "box-widget--default";

  return (
    <div
      className={[
        "box-widget-container",
        "box-widget",
        cursorClass,
        backgroundType === "solid"
          ? "box-widget--solid"
          : "box-widget--gradient",
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseDown={() => draggable && setCursorState("grabbing")}
      onMouseUp={() => draggable && setCursorState("grab")}
      onMouseLeave={() => draggable && setCursorState("grab")}
      draggable={draggable}
      style={cssVars}
      onDragStart={(e) => {
        const widgetEl = e.target.closest(".widget");
        if (!widgetEl) return;

        const rect = widgetEl.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        const wid = widgetEl.id;
        const action = wid.startsWith("base") ? "add" : "move";

        const dragData = `box-widget;${wid};${offsetX};${offsetY};${action}`;
        e.dataTransfer.setData("text/plain", dragData);
      }}
    >
      {showLabel ? <div className="box-widget-label">{title}</div> : null}
    </div>
  );
};

export default BoxWidget;
